import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SHRTLogo } from '@/components/SHRTLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Lock, ArrowRight, KeyRound, HelpCircle, Eye, EyeOff, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, requestPasswordSetup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get return URL from query params
  const returnUrl = searchParams.get('returnUrl') || searchParams.get('return') || null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Willkommen zurück!');
      
      // Check for return URL first - decode if needed
      const decodedReturnUrl = returnUrl ? decodeURIComponent(returnUrl) : null;
      console.log('Return URL:', returnUrl, 'Decoded:', decodedReturnUrl);
      
      if (decodedReturnUrl) {
        navigate(decodedReturnUrl);
      } else {
        navigate('/home');
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handlePasswordSetupRequest = async (e) => {
    e.preventDefault();
    setSetupLoading(true);
    
    const result = await requestPasswordSetup(setupEmail);
    
    if (result.success) {
      toast.success('Falls Ihre E-Mail-Adresse registriert ist, erhalten Sie einen Link zur Passwort-Einrichtung.');
      setShowPasswordSetup(false);
      setSetupEmail('');
    } else {
      toast.error(result.error);
    }
    
    setSetupLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSetupLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success('Falls Ihre E-Mail-Adresse registriert ist, erhalten Sie einen Link zum Zurücksetzen des Passworts.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    }
    
    setSetupLoading(false);
  };

  const resetToLogin = () => {
    setShowPasswordSetup(false);
    setShowForgotPassword(false);
    setSetupEmail('');
    setForgotEmail('');
  };

  return (
    <div className="min-h-screen split-screen">
      {/* Left Side - Hero Image */}
      <div 
        className="relative bg-cover bg-center hero-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1730017474098-49f10e52294f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwcmFjaW5nJTIwY2FyJTIwYmxhY2slMjBhbmQlMjB3aGl0ZXxlbnwwfHx8fDE3NjkyMDMwNzR8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30 z-10" />
        <div className="relative z-20 h-full flex flex-col justify-end p-12">
          <div className="max-w-md">
            <SHRTLogo size="lg" className="mb-6" />
            <h1 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4">
              Swiss Historic Racing Team
            </h1>
            <p className="text-white/70 text-lg">
              Willkommen im exklusiven Mitgliederbereich
            </p>
            <div className="mt-8 h-[2px] w-32 bg-primary" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="bg-background flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="md:hidden mb-8 flex items-center gap-3">
            <SHRTLogo size="sm" />
            <div>
              <p className="font-['Oswald'] text-lg font-bold tracking-wider text-foreground">S.H.R.T.</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Mitgliederbereich</p>
            </div>
          </div>

          {/* First Time Here? - Top */}
          <Link 
            to="/install"
            className="flex items-center justify-center gap-2 w-full mb-6 px-4 py-3 border border-primary/50 text-primary hover:bg-primary/10 transition-colors text-sm"
            data-testid="install-guide-link"
          >
            <span>📱</span>
            <span>Zum ersten Mal hier? <strong>App installieren</strong></span>
          </Link>

          {!showPasswordSetup && !showForgotPassword ? (
            <>
              <div className="mb-8">
                <h2 className="font-['Oswald'] text-3xl font-bold text-foreground uppercase tracking-tight mb-2">
                  Anmelden
                </h2>
                <p className="text-muted-foreground">
                  Melden Sie sich mit Ihrer E-Mail-Adresse an
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                    E-Mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.ch"
                      className="pl-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Passwort
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-12 pr-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none btn-glow transition-all duration-300"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Anmelden
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-border space-y-3">
                <button
                  onClick={() => { setShowForgotPassword(true); setShowPasswordSetup(false); }}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="show-forgot-password"
                >
                  <HelpCircle size={16} />
                  Passwort vergessen?
                </button>
                <button
                  onClick={() => { setShowPasswordSetup(true); setShowForgotPassword(false); }}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="show-password-setup"
                >
                  <KeyRound size={16} />
                  Erstes Mal hier? Passwort einrichten
                </button>
                <Link
                  to="/mitglied-werden"
                  className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                  data-testid="membership-request-link"
                >
                  <UserPlus size={16} />
                  Mitglied werden
                </Link>
              </div>
            </>
          ) : showForgotPassword ? (
            <>
              <div className="mb-8">
                <h2 className="font-['Oswald'] text-3xl font-bold text-foreground uppercase tracking-tight mb-2">
                  Passwort vergessen
                </h2>
                <p className="text-muted-foreground">
                  Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen des Passworts.
                </p>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                    E-Mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="ihre@email.ch"
                      className="pl-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={setupLoading}
                  className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none btn-glow transition-all duration-300"
                  data-testid="forgot-submit"
                >
                  {setupLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Link anfordern
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-border">
                <button
                  onClick={resetToLogin}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="back-to-login"
                >
                  ← Zurück zur Anmeldung
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-['Oswald'] text-3xl font-bold text-foreground uppercase tracking-tight mb-2">
                  Passwort einrichten
                </h2>
                <p className="text-muted-foreground">
                  Geben Sie Ihre registrierte E-Mail-Adresse ein. Sie erhalten einen Link zur Passwort-Einrichtung.
                </p>
              </div>

              <form onSubmit={handlePasswordSetupRequest} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="setup-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                    E-Mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="setup-email"
                      type="email"
                      value={setupEmail}
                      onChange={(e) => setSetupEmail(e.target.value)}
                      placeholder="ihre@email.ch"
                      className="pl-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                      required
                      data-testid="setup-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={setupLoading}
                  className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none btn-glow transition-all duration-300"
                  data-testid="setup-submit"
                >
                  {setupLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Link anfordern
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-border">
                <button
                  onClick={resetToLogin}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="back-to-login"
                >
                  ← Zurück zur Anmeldung
                </button>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-muted-foreground">
            <div className="leading-relaxed mb-3">
              <p className="font-bold text-foreground uppercase tracking-wider">S.H.R.T.</p>
              <p>Swiss Historic Racing Team</p>
              <p className="text-[10px]">Verein gem&auml;ss Art. 60 ff. ZGB</p>
              <p className="mt-2">Kuno Sch&auml;r &middot; St. Niklausstrasse 1 &middot; CH-4502 Solothurn</p>
              <p>Tel. <a href="tel:+41794153031" className="hover:text-foreground">+41 79 415 30 31</a> &middot; <a href="mailto:info@kunoschaer.ch" className="hover:text-foreground">info@kunoschaer.ch</a></p>
            </div>
            <div className="flex justify-center gap-3 mb-2">
              <Link to="/datenschutz" className="hover:text-foreground uppercase tracking-wider">Datenschutz</Link>
              <span>|</span>
              <Link to="/agb" className="hover:text-foreground uppercase tracking-wider">AGB</Link>
              <span>|</span>
              <Link to="/impressum" className="hover:text-foreground uppercase tracking-wider">Impressum</Link>
            </div>
            <p className="uppercase tracking-wider">
              &copy; {new Date().getFullYear()} Swiss Historic Racing Team
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
